/**
 * AI prompt provider constants.
 *
 * The full `AiPromptProvider` Prisma enum also carries providers that cannot
 * run chat prompts (COHERE is rerank-only, ZEROENTROPY is retired). Chat
 * prompts may only run on the providers below — the server's
 * `aiPromptChatProviderSchema` derives from this list, and client prompt
 * builders use `toAiPromptChatProvider` to narrow before calling chat routes.
 */
export const AI_PROMPT_CHAT_PROVIDERS = ['OPENAI', 'GOOGLE', 'ANTHROPIC'] as const;

export type AiPromptChatProvider = (typeof AI_PROMPT_CHAT_PROVIDERS)[number];

/** Narrow an arbitrary provider value to a chat provider, or `undefined`. */
export function toAiPromptChatProvider(value: string | null | undefined): AiPromptChatProvider | undefined {
    return AI_PROMPT_CHAT_PROVIDERS.find((provider) => provider === value);
}

/**
 * CUR-1532 — the Handlebars helper a stored prompt's USER message uses to mark a
 * provider prompt-cache boundary. The server splits the stored template on the
 * marker BEFORE interpolating variables and sends the pieces as ordered text
 * parts, the ones ahead of a marker flagged as an explicit OpenAI cache
 * breakpoint. Every whole-template renderer (previews, contract checks,
 * evaluations) reaches the registered no-op helper instead, so the marker
 * contributes no text anywhere.
 */
export const AI_PROMPT_CACHE_BREAKPOINT_HELPER = 'cacheBreakpoint';

/** The marker exactly as a stored message must spell it — no whitespace control, no arguments. */
export const AI_PROMPT_CACHE_BREAKPOINT_MARKER = `{{${AI_PROMPT_CACHE_BREAKPOINT_HELPER}}}`;

/** The most markers one stored prompt may carry. */
export const AI_PROMPT_MAX_CACHE_BREAKPOINTS = 3;
